/*
 * argile-projet-couleurs.js — Ajout groupé des testeurs d'un projet au panier.
 *
 * UNE requête `/cart/add` multi-lignes, pas une par teinte : N appels séquentiels
 * laisseraient le panier à moitié rempli si l'un échoue au milieu, et N appels
 * parallèles se marcheraient dessus sur le même panier.
 *
 * ⚠️ INVARIANT DU 2026-07-20 (cf. sections/argile-product.liquid) : « échantillon
 * rendu » ne veut pas dire « échantillon vendable ». Le Liquid rend TOUTES les cartes
 * et porte la disponibilité sur `data-sample-available`. On filtre donc là-dessus,
 * jamais sur la simple présence de `data-sample-id`.
 *
 * Le protocole de mise à jour du panier est celui d'`argile-product.js` : on demande
 * les sections `cart-items` dans la requête d'ajout, on relit `/cart.js`, puis on émet
 * `cart:update` (nom natif = ThemeEvents.cartUpdate) avec les sections rendues, pour
 * que le tiroir morphe sans re-fetch. S'en écarter laisserait la pastille du panier
 * à jour côté serveur mais figée à l'écran.
 */
(() => {
  const OUVERTURE_SECOURS_MS = 1200;

  const init = (root) => {
    const bouton = root.querySelector('[data-couleurs-ajout-groupe]');
    if (!bouton || bouton.dataset.initialise === '1') return;
    bouton.dataset.initialise = '1';

    const statut = root.querySelector('[data-couleurs-statut]');

    // Chaque ligne emporte le nom de SA teinte (propriété visible, libellé calculé en
    // Liquid par snippets/argile-teinte-label.liquid) et son handle (caché, sert au
    // panier à renvoyer vers la page de la teinte). Attribut absent → pas de propriété.
    const proprietes = (el) => {
      const label = bouton.dataset.teinteLabel;
      const { teinteTitle: titre, teinteHandle: handle } = el.dataset;
      if (!label || !titre || !handle) return null;
      return { [label]: titre, _teinte_handle: handle };
    };

    const lignesDisponibles = () =>
      Array.from(root.querySelectorAll('[data-couleurs-teinte]'))
        .filter((el) => el.dataset.sampleAvailable === 'true')
        .map((el) => {
          const id = Number(el.dataset.sampleId);
          const props = proprietes(el);
          return props ? { id, quantity: 1, properties: props } : { id, quantity: 1 };
        })
        .filter(({ id }) => Number.isFinite(id) && id > 0);

    bouton.addEventListener('click', async () => {
      const lignes = lignesDisponibles();
      if (!lignes.length) return;

      bouton.setAttribute('aria-busy', 'true');
      if (statut) statut.textContent = '';

      // Sections du tiroir à re-rendre côté serveur, comme sur la fiche produit.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset?.sectionId)
        .filter(Boolean);

      // Ouverture du tiroir : une seule fois, soit quand le cross-sell a fini de
      // charger, soit au secours. On n'ouvre pas un panneau incomplet.
      let ouvert = false;
      const ouvrirTiroir = () => {
        if (ouvert) return;
        ouvert = true;
        document.removeEventListener('argile:recos-ready', ouvrirTiroir);
        bouton.removeAttribute('aria-busy');
        document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
      };

      try {
        const urlAjout = window.Theme?.routes?.cart_add_url || '/cart/add';
        const reponse = await fetch(urlAjout, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items: lignes,
            sections: sectionIds.join(','),
          }),
        });
        if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
        const res = await reponse.json();

        const panier = await (await fetch('/cart.js')).json();

        document.addEventListener('argile:recos-ready', ouvrirTiroir);
        setTimeout(ouvrirTiroir, OUVERTURE_SECOURS_MS);

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: panier,
              sourceId: 'argile-projet-couleurs',
              data: {
                source: 'argile-projet-couleurs',
                itemCount: panier.item_count,
                sections: res.sections,
              },
            },
          })
        );
      } catch (err) {
        // Message VISIBLE, pas seulement une trace console : sans retour, l'utilisateur
        // reclique et double sa commande.
        if (statut) statut.textContent = bouton.dataset.erreur || '';
        bouton.removeAttribute('aria-busy');
        console.error('[argile-projet-couleurs] ajout groupé échoué', err);
      }
    });
  };

  const initTout = (portee = document) =>
    portee.querySelectorAll('[data-projet-couleurs]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTout());
  } else {
    initTout();
  }

  // Ré-initialisation dans l'éditeur : une section rechargée perd ses écouteurs.
  document.addEventListener('shopify:section:load', (e) => initTout(e.target));
})();
